import { useMemo, useState } from 'react';
import { Table, Thead, Tbody, Tr, Th, Td } from '@parrot/ui';
import { MetricCell } from '@/components/primitives';
import { formatArpu, formatCount, formatPct, formatUsdCompact, prettyQuarter } from '@/lib/format';
import styles from './QuarterlyPerformanceTable.module.css';

export interface QuarterlyRow {
  quarter: string;
  region: string;
  subscribers: number;
  arpu: number;
  revenue: number;
  gross_additions: number;
  /** null where the source can't derive it (the oldest quarter). */
  churns: number | null;
  net_additions: number | null;
  churn_rate: number | null;
}

export interface QuarterlyPerformanceTableProps {
  rows: QuarterlyRow[];
  /** Rows before "Load all". */
  initialRows?: number;
}

type SortKey = 'quarter' | 'subscribers' | 'arpu' | 'revenue' | 'churns' | 'gross_additions' | 'net_additions' | 'churn_rate';
type Sort = { key: string; direction: 'asc' | 'desc' };

/** "+6.42M" / "−1.10M" — signed, true minus. */
export function formatSignedCount(v: number): string {
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatCount(Math.abs(v))}`;
}

const NA = <span className={styles.na}>—</span>;

/**
 * One row per quarter (× region when split), after the old Parrot platform
 * page, on the @parrot/ui Table. Each metric column keeps one tone and a bar
 * scaled to that column's max within the visible rows, so the eye can run
 * down a column. Every column sorts; quarter, newest first, by default.
 */
export function QuarterlyPerformanceTable({ rows, initialRows = 8 }: QuarterlyPerformanceTableProps) {
  const [showAll, setShowAll] = useState(false);
  const [sort, setSort] = useState<Sort>({ key: 'quarter', direction: 'desc' });

  // The Table never reorders rows — that's ours. Missing values sort last.
  const sorted = useMemo(() => {
    const key = sort.key as SortKey;
    const dir = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      if (key === 'quarter') return dir * (a.quarter.localeCompare(b.quarter) || a.region.localeCompare(b.region));
      const av = a[key];
      const bv = b[key];
      if (av == null) return 1;
      if (bv == null) return -1;
      return dir * (av - bv);
    });
  }, [rows, sort]);
  const visible = showAll ? sorted : sorted.slice(0, initialRows);

  const max = (pick: (r: QuarterlyRow) => number | null) =>
    visible.reduce((m, r) => Math.max(m, Math.abs(pick(r) ?? 0)), 0) || 1;
  const maxRevenue = max((r) => r.revenue);
  const maxChurns = max((r) => r.churns);
  const maxGross = max((r) => r.gross_additions);
  const maxNet = max((r) => r.net_additions);

  return (
    <div className={styles.wrap}>
      <Table sort={sort} onSortChange={setSort} className={styles.table}>
        <Thead>
          <Tr>
            <Th sortKey="quarter" sortFirst="desc" className={styles.pinned}>
              Time Period
            </Th>
            <Th>Region</Th>
            <Th sortKey="subscribers" sortFirst="desc">Subscribers</Th>
            <Th sortKey="arpu" sortFirst="desc">ARPU</Th>
            <Th sortKey="revenue" sortFirst="desc">Revenue</Th>
            <Th sortKey="churns" sortFirst="desc">Churns</Th>
            <Th sortKey="gross_additions" sortFirst="desc">Gross Additions</Th>
            <Th sortKey="net_additions" sortFirst="desc">Net Additions</Th>
            <Th sortKey="churn_rate" sortFirst="asc">Churn Rate</Th>
          </Tr>
        </Thead>
        <Tbody>
          {visible.map((r) => {
            const net = r.net_additions;
            return (
              <Tr key={`${r.quarter}-${r.region}`}>
                <Td className={styles.pinned}>
                  <span className={styles.quarter}>{prettyQuarter(r.quarter)}</span>
                </Td>
                <Td>{r.region}</Td>
                <Td>
                  <MetricCell
                    value={formatCount(r.subscribers)}
                    sub={net != null ? `${formatSignedCount(net)} ${net > 0 ? '▲' : net < 0 ? '▼' : ''}` : undefined}
                    subTone={net == null || net === 0 ? 'muted' : net > 0 ? 'good' : 'bad'}
                  />
                </Td>
                <Td>
                  <MetricCell value={formatArpu(r.arpu)} />
                </Td>
                <Td>
                  <MetricCell value={formatUsdCompact(r.revenue)} fraction={r.revenue / maxRevenue} tone="revenue" />
                </Td>
                <Td>
                  {r.churns != null ? (
                    <MetricCell value={formatCount(r.churns)} fraction={r.churns / maxChurns} tone="decline" />
                  ) : (
                    NA
                  )}
                </Td>
                <Td>
                  <MetricCell
                    value={formatCount(r.gross_additions)}
                    fraction={r.gross_additions / maxGross}
                    tone="acquired"
                  />
                </Td>
                <Td>
                  {net != null ? (
                    <MetricCell value={formatSignedCount(net)} fraction={Math.abs(net) / maxNet} tone="share" />
                  ) : (
                    NA
                  )}
                </Td>
                <Td>{r.churn_rate != null ? <MetricCell value={formatPct(r.churn_rate)} /> : NA}</Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
      {rows.length > initialRows && (
        <button type="button" data-print-hide className={styles.loadAll} onClick={() => setShowAll((v) => !v)}>
          {showAll ? 'Show fewer' : `Load all (${rows.length})`}
        </button>
      )}
    </div>
  );
}
